<template>
    <section v-if="evaluation" class="initial-evaluation" aria-live="polite">
        <div class="initial-header">
            <strong>{{ $t('현재 AI 평가') }}</strong>
            <div class="initial-badges">
                <b-badge v-if="isLegacy" variant="secondary">{{ $t('기존 평가 근거') }}</b-badge>
                <b-badge v-if="isReconstructed" variant="warning">{{ $t('재구성된 평가 과정') }}</b-badge>
                <b-badge v-if="hasSatisfaction" :variant="evaluation.isSatisfied ? 'success' : 'danger'">
                    {{ evaluation.isSatisfied ? $t('충족 (Y)') : $t('미충족 (N)') }}
                </b-badge>
            </div>
        </div>

        <p v-if="isLegacy" class="source-notice">
            {{ $t('이 평가는 구조화된 평가 과정이 저장되기 전에 생성되어, 당시 저장된 평가 근거만 표시합니다.') }}
        </p>
        <p v-if="isReconstructed" class="source-notice reconstructed-notice">
            {{ $t('현재 저장된 평가 근거를 바탕으로 사용자 공개용 평가 과정을 재구성했습니다. 원래 생성 시점의 기록과 다를 수 있습니다.') }}
        </p>

        <dl class="result-summary">
            <template v-if="categoryText">
                <dt>{{ $t('판정 분류') }}</dt>
                <dd>{{ categoryText }}</dd>
            </template>
            <template v-if="hasConfidence">
                <dt>{{ $t('신뢰도') }}</dt>
                <dd>{{ confidenceText }}</dd>
            </template>
        </dl>

        <div v-if="reasonComment" class="evaluation-section">
            <h6>{{ isLegacy ? $t('기존 평가 근거') : $t('판정 근거') }}</h6>
            <p>{{ reasonComment }}</p>
        </div>

        <template v-if="process && !isLegacy">
            <div v-if="process.criterionInterpretation" class="evaluation-section">
                <h6>{{ $t('평가 기준 해석') }}</h6>
                <p>{{ process.criterionInterpretation }}</p>
            </div>

            <div v-if="process.evidenceChecks && process.evidenceChecks.length" class="evaluation-section">
                <h6>{{ $t('확인 근거') }}</h6>
                <article v-for="(check, index) in process.evidenceChecks"
                         :key="`evidence-check-${index}`"
                         class="evidence-check"
                >
                    <div v-if="check.filename"><strong>{{ $t('파일') }}:</strong> {{ check.filename }}</div>
                    <div v-if="check.location"><strong>{{ $t('위치') }}:</strong> {{ check.location }}</div>
                    <div v-if="check.finding"><strong>{{ $t('확인 내용') }}:</strong> {{ check.finding }}</div>
                    <div v-if="check.relevance"><strong>{{ $t('평가 기준과의 관계') }}:</strong> {{ check.relevance }}</div>
                </article>
            </div>

            <div v-if="process.ruleApplication" class="evaluation-section">
                <h6>{{ $t('판정 규칙 적용') }}</h6>
                <p>{{ process.ruleApplication }}</p>
            </div>

            <div v-if="process.limitations && process.limitations.length" class="evaluation-section">
                <h6>{{ $t('확인 한계') }}</h6>
                <ul>
                    <li v-for="(limitation, index) in process.limitations" :key="`limitation-${index}`">
                        {{ limitation }}
                    </li>
                </ul>
            </div>

            <div v-if="process.conclusion" class="evaluation-section">
                <h6>{{ $t('결론') }}</h6>
                <p>{{ process.conclusion }}</p>
            </div>
        </template>

        <div v-if="reasonEvidence.length" class="evaluation-section">
            <h6>{{ $t('저장된 근거') }}</h6>
            <ul>
                <li v-for="(evidence, index) in reasonEvidence" :key="`reason-evidence-${index}`">
                    {{ formatEvidence(evidence) }}
                </li>
            </ul>
        </div>
    </section>
</template>

<script>
export default {
    name: 'AIQnaInitialEvaluation',
    props: {
        initialMessage: {
            type: Object,
            default: null,
        },
    },
    computed: {
        evaluation() {
            return this.initialMessage?.currentEvaluation || null;
        },
        process() {
            return this.evaluation?.evaluationProcess || null;
        },
        isLegacy() {
            return this.initialMessage?.source === 'legacy_reason';
        },
        isReconstructed() {
            return this.initialMessage?.isReconstructed === true
                || this.initialMessage?.source === 'reconstructed';
        },
        hasSatisfaction() {
            return typeof this.evaluation?.isSatisfied === 'boolean';
        },
        hasConfidence() {
            return typeof this.evaluation?.confidence === 'number';
        },
        confidenceText() {
            if (!this.hasConfidence) return '';
            return `${Math.round(this.evaluation.confidence * 100)}%`;
        },
        categoryText() {
            const category = this.evaluation?.reason?.category;
            if (!category) return '';
            if (category.code && category.category) return `${category.category} (${category.code})`;
            return category.category || category.code || '';
        },
        reasonComment() {
            return this.evaluation?.reason?.comment || '';
        },
        reasonEvidence() {
            return Array.isArray(this.evaluation?.reason?.evidence)
                ? this.evaluation.reason.evidence
                : [];
        },
    },
    methods: {
        formatEvidence(evidence) {
            if (typeof evidence === 'string') return evidence;
            if (evidence === null || evidence === undefined) return '';
            if (typeof evidence !== 'object') return String(evidence);
            return Object.values(evidence)
                .filter((value) => ['string', 'number', 'boolean'].includes(typeof value))
                .join(' · ');
        },
    },
};
</script>

<style scoped lang="scss">
.initial-evaluation {
    padding: 12px;
    border: 1px solid #dce3ea;
    border-radius: 10px;
    background: #f8fafc;
    color: #343a40;
}

.initial-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
}

.initial-badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 5px;
}

.source-notice {
    margin: 0 0 10px;
    padding: 8px;
    border-radius: 6px;
    background: #e9ecef;
    font-size: 0.78rem;
}

.reconstructed-notice {
    background: #fff3cd;
}

.result-summary {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 3px 10px;
    margin: 0 0 10px;
    font-size: 0.8rem;

    dt,
    dd {
        margin: 0;
    }
}

.evaluation-section {
    margin-top: 10px;

    h6 {
        margin-bottom: 4px;
        color: #495057;
        font-size: 0.78rem;
        font-weight: 700;
    }

    p,
    ul {
        margin-bottom: 0;
        white-space: pre-wrap;
    }

    ul {
        padding-left: 18px;
    }
}

.evidence-check {
    margin-top: 6px;
    padding: 8px;
    border-left: 3px solid #adb5bd;
    background: #fff;
    font-size: 0.78rem;
    word-break: break-word;
}
</style>
